import React from "react";
import * as R from "ramda";
import CountUp from "react-countup";

import Text, { useGetText } from "components/Text";
import Tooltip from "components/Tooltip";
import { useAnswerContext } from "contexts/answerContext";
import { useFeesList } from "queries/entities";
import { useRequirements } from "queries/requirements";
import { maybeMoney } from "utils/format";
import { filterByIds } from "utils/func";

import styles from "./Fee.scss";

const defaultCountUpProps = {
  duration: 1,
  delay: 0,
  decimals: 2,
  preserveValue: true,
  prefix: "$",
  separator: ",",
};

const MaybeCountUp = ({ value, animate }) => {
  if (!value || !animate) return maybeMoney(value);
  return <CountUp end={value} {...defaultCountUpProps} />;
};

const BaseFeesTotal = ({
  feeIDs,
  requirements,
  calloutPartial = true,
  hideZero = false,
  animate = false,
}) => {
  const { record } = useAnswerContext();
  const getText = useGetText();

  const fees = R.pick(feeIDs, record.calculated_fee_totals);
  const total = R.sum(R.values(fees));
  const isPartial = feeIDs.some((id) => (record.unsatisfied_fee_ids || []).includes(id));
  const hasExternalFees = R.any(R.propEq(true, "fees_calculated_externally"), requirements);
  const hasNoFees = R.values(fees).length === 0;

  if (hideZero && total === 0 && isPartial) return "...";

  const showTotal = calloutPartial && isPartial;

  if (hasExternalFees) return <Text t="guides.summary_table.cost_varies" />;
  if (hasNoFees) return <Text t="guides.summary_table.no_fees" />;

  return (
    <div className={styles.container}>
      <MaybeCountUp value={total} animate={animate} />
      {showTotal && <Tooltip icon="circle-info" message={getText("fees.incomplete_tooltip")} />}
    </div>
  );
};

const FeesTotal = ({ feeIDs, ...props }) => {
  const { data: requirementIDs = [] } = useFeesList({
    select: R.pipe(filterByIds(feeIDs), R.map(R.prop("requirement_id")), R.uniq),
  });
  const { data: requirements = [] } = useRequirements({ select: filterByIds(requirementIDs) });

  return <BaseFeesTotal feeIDs={feeIDs} requirements={requirements} {...props} />;
};

export const FeeTotal = ({ feeID, ...props }) => <FeesTotal feeIDs={[feeID]} {...props} />;

export const RequirementsTotal = ({ requirementIDs, ...props }) => {
  const { data: requirements = [] } = useRequirements({
    select: filterByIds(requirementIDs),
  });

  const feeIDs = R.chain(R.propOr([], "fee_ids"), requirements);
  return <BaseFeesTotal feeIDs={feeIDs} requirements={requirements} {...props} />;
};

export const RequirementTotal = ({ requirementID, ...props }) => (
  <RequirementsTotal requirementIDs={[requirementID]} {...props} />
);
